"use client"

import type React from "react"
import { useState, useEffect, useCallback, useRef } from "react"
import { useLocation, Link } from "react-router-dom"
import { FiArrowLeft, FiMapPin, FiUsers, FiStar, FiShield, FiEdit, FiCalendar, FiX, FiPlus, FiTrash2, FiSearch, FiEye, FiSave } from "react-icons/fi"
import Breadcrumb from "../../utils/breadcrumbs"
import Loader from "../../utils/loader"
import Pagination from "../../utils/pagination2"
import ConfirmationModal from "../../ui/confirmation-modal"
import api from "../../libs/axios"
import formatDate from "../../utils/format-date"
import toast from "react-hot-toast"

interface IPlayer {
    id: number
    phone_number: string
    role_id: number
    role_name: string
    is_in_team?: boolean
    details: {
        first_name: string
        last_name: string
    }
    player_name?: string
    player_role?: number
    jersey_number?: number | null
    is_captain?: boolean
    is_wicket_keeper?: boolean
    batting_style?: string | number
    bowling_style?: string | number
}

interface IPlayerRole {
    id: number
    name: string
}

interface IBattingStyle {
    id: number
    name: string
}

interface IBowlingStyle {
    id: number
    name: string
}

interface IOwner {
    id: number
    phone_number: string
    details: {
        first_name: string
        last_name: string
    }
}

interface ITeam {
    id: number
    name: string
    logo_url: string | null
    location: string
    owner_id: number
    is_active: boolean
    created_at: string
    updated_at: string
    players?: IPlayer[]
}

interface ITeamDetailsResponse {
    team: ITeam
    owner: IOwner
}

interface IPagination {
    current_page: number
    per_page: number
    total: number
    total_pages: number
    has_next_page: boolean
    has_prev_page: boolean
    next_page: number | null
    prev_page: number | null
}

const TeamView: React.FC = () => {
    const location = useLocation()
    const [teamData, setTeamData] = useState<ITeamDetailsResponse | null>(null)
    const [loading, setLoading] = useState(true)
    const [isOffcanvasOpen, setIsOffcanvasOpen] = useState(false)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [isEditModalOpen, setIsEditModalOpen] = useState(false)
    const [availablePlayers, setAvailablePlayers] = useState<IPlayer[]>([])
    const [selectedPlayers, setSelectedPlayers] = useState<IPlayer[]>([])
    const [editingPlayer, setEditingPlayer] = useState<IPlayer | null>(null)
    const [isAddingPlayers, setIsAddingPlayers] = useState(false)
    const [isUpdatingPlayer, setIsUpdatingPlayer] = useState(false)
    const [isDeletingPlayer, setIsDeletingPlayer] = useState(false)
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
    const [playerToDelete, setPlayerToDelete] = useState<number | null>(null)
    const [searchQuery, setSearchQuery] = useState("")
    const [isLoadingPlayers, setIsLoadingPlayers] = useState(false)
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)
    
    const [pagination, setPagination] = useState<IPagination>({
        current_page: 1,
        per_page: 20,
        total: 0,
        total_pages: 1,
        has_next_page: false,
        has_prev_page: false,
        next_page: null,
        prev_page: null
    })

    const [teamPlayersPagination, setTeamPlayersPagination] = useState<IPagination>({
        current_page: 1,
        per_page: 10,
        total: 0,
        total_pages: 1,
        has_next_page: false,
        has_prev_page: false,
        next_page: null,
        prev_page: null
    })

    const [teamPlayers, setTeamPlayers] = useState<IPlayer[]>([])
    const [playerRoles, setPlayerRoles] = useState<IPlayerRole[]>([])
    const [battingStyles, setBattingStyles] = useState<IBattingStyle[]>([])
    const [bowlingStyles, setBowlingStyles] = useState<IBowlingStyle[]>([])

    const team_id = location.state?.team_id

    const getPlayerRole = (roleId: number): string => {
        const role = playerRoles.find(r => r.id === roleId)
        return role ? role.name : "Unknown"
    }

    const fetchDropdownData = async () => {
        try {
            const [rolesRes, battingRes, bowlingRes] = await Promise.all([
                api.get('/dropdown?category=player_role'),
                api.get('/dropdown?category=batting_style'),
                api.get('/dropdown?category=bowling_style')
            ])

            setPlayerRoles(rolesRes.data.data || [])
            setBattingStyles(battingRes.data.data || [])
            setBowlingStyles(bowlingRes.data.data || [])
        } catch (err: any) {
            console.error("Error fetching dropdown data:", err)
        }
    }

    const fetchAvailablePlayers = useCallback(async (page: number = 1, limit: number = 20, search: string = "") => {
        try {
            setIsLoadingPlayers(true)
            const params = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString(),
            })

            // Only add search param if it's not empty
            if (search && search.trim() !== '') {
                params.append('search', search.trim())
            }

            const response = await api.get(`/teams/${team_id}/players?${params}`)
            const playersData = response.data?.data || []
            const paginationData = response.data?.pagination

            const formattedPlayers = playersData.map((player: any) => ({
                ...player,
                player_name: `${player.details.first_name} ${player.details.last_name}`,
                player_role: player.role_id,
                jersey_number: null,
                is_captain: false,
                is_wicket_keeper: false,
            }))

            setAvailablePlayers(formattedPlayers)
            if (paginationData) {
                setPagination(paginationData)
            }
        } catch (err: any) {
            console.error("Error fetching players:", err)
            toast.error(err?.response?.data?.message || "Failed to fetch players")
            setAvailablePlayers([])
        } finally {
            setIsLoadingPlayers(false)
        }
    }, [team_id])

    const handleAddSelectedPlayer = (player: IPlayer) => {
        if (!selectedPlayers.some((p) => p.id === player.id)) {
            const playerWithDefaults = {
                ...player,
                player_role: player.role_id,
                jersey_number: null,
                is_captain: false,
                is_wicket_keeper: false,
                batting_style: battingStyles[0]?.id.toString(),
                bowling_style: bowlingStyles[0]?.id.toString()
            }
            setSelectedPlayers([...selectedPlayers, playerWithDefaults])
        }
    }

    const handleRemoveSelectedPlayer = (playerId: number) => {
        setSelectedPlayers(selectedPlayers.filter((p) => p.id !== playerId))
    }

    const updateSelectedPlayer = (playerId: number, field: string, value: any) => {
        setSelectedPlayers(selectedPlayers.map(player =>
            player.id === playerId ? { ...player, [field]: value } : player
        ))
    }

    const handleEditPlayer = (player: IPlayer) => {
        setEditingPlayer({
            ...player,
            player_role: player.player_role || playerRoles[0]?.id,
            batting_style: player.batting_style || battingStyles[0]?.id,
            bowling_style: player.bowling_style || bowlingStyles[0]?.id
        })
        setIsEditModalOpen(true)
    }

    const updateEditingPlayer = (field: string, value: any) => {
        setEditingPlayer(prev => prev ? { ...prev, [field]: value } : null)
    }

    const savePlayerChanges = async () => {
        if (!editingPlayer) return

        try {
            setIsUpdatingPlayer(true)

            const updateData = {
                player_name: editingPlayer.player_name,
                player_role: parseInt(editingPlayer.player_role?.toString() || '0'),
                batting_style: parseInt(editingPlayer.batting_style?.toString() || '0'),
                bowling_style: parseInt(editingPlayer.bowling_style?.toString() || '0'),
                jersey_number: editingPlayer.jersey_number ? parseInt(editingPlayer.jersey_number.toString()) : null,
                is_captain: Boolean(editingPlayer.is_captain),
                is_wicket_keeper: Boolean(editingPlayer.is_wicket_keeper)
            }

            await api.put(`/teams/${team_id}/players/${editingPlayer.id}`, updateData)

            toast.success("Player updated successfully")
            setIsEditModalOpen(false)
            setEditingPlayer(null)
            fetchTeamDetails(teamPlayersPagination.current_page, teamPlayersPagination.per_page)
        } catch (err: any) {
            console.error("Error updating player:", err)
            toast.error(err?.response?.data?.message || "Failed to update player")
        } finally {
            setIsUpdatingPlayer(false)
        }
    }

    const removePlayerFromTeam = async (playerId: number) => {
        setPlayerToDelete(playerId)
        setIsDeleteModalOpen(true)
    }

    const confirmDeletePlayer = async () => {
        if (!playerToDelete) return

        try {
            setIsDeletingPlayer(true)
            await api.delete(`/teams/${team_id}/players/${playerToDelete}`)
            toast.success("Player removed successfully")
            fetchTeamDetails(teamPlayersPagination.current_page, teamPlayersPagination.per_page)
        } catch (err: any) {
            console.error("Error removing player:", err)
            toast.error(err?.response?.data?.message || "Failed to remove player")
        } finally {
            setIsDeletingPlayer(false)
            setIsDeleteModalOpen(false)
            setPlayerToDelete(null)
        }
    }

    const cancelDeletePlayer = () => {
        setIsDeleteModalOpen(false)
        setPlayerToDelete(null)
    }

    const addPlayersToTeam = async () => {
        if (!team_id || selectedPlayers.length === 0) return

        try {
            setIsAddingPlayers(true)
            const playersToAdd = selectedPlayers.map((player) => ({
                user_id: player.id,
                player_name: `${player.details.first_name} ${player.details.last_name}`,
                player_role: parseInt(player.player_role?.toString() || player.role_id.toString()),
                jersey_number: player.jersey_number ? parseInt(player.jersey_number.toString()) : null,
                is_captain: Boolean(player.is_captain),
                is_wicket_keeper: Boolean(player.is_wicket_keeper),
                batting_style: parseInt(player.batting_style?.toString() || battingStyles[0]?.id.toString()),
                bowling_style: parseInt(player.bowling_style?.toString() || bowlingStyles[0]?.id.toString())
            }))

            await api.post(`/teams/${team_id}/players/multiple`, {
                players: playersToAdd,
            })
            toast.success("Players added to team successfully")
            setSelectedPlayers([])
            setIsOffcanvasOpen(false)
            setIsModalOpen(false)
            fetchTeamDetails(teamPlayersPagination.current_page, teamPlayersPagination.per_page)
        } catch (err: any) {
            console.error("Error adding players:", err)
            toast.error(err?.response?.data?.message || "Failed to add players")
        } finally {
            setIsAddingPlayers(false)
        }
    }

    const fetchTeamDetails = async (page: number = 1, limit: number = 10) => {
        if (!team_id) {
            setLoading(false)
            return
        }

        try {
            setLoading(true)
            const params = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString(),
            })
            const response = await api.get(`/teams/${team_id}?${params}`)

            setTeamData({
                team: response.data.team,
                owner: response.data.owner
            })

            setTeamPlayers(response.data.players?.data || [])

            if (response.data.players?.pagination) {
                setTeamPlayersPagination(response.data.players.pagination)
            }
        } catch (err: any) {
            console.error("Error fetching team details:", err)
            toast.error("Failed to fetch team details")
        } finally {
            setLoading(false)
        }
    }

    const handlePageChange = (page: number) => {
        fetchAvailablePlayers(page, pagination.per_page, searchQuery)
    }

    const handleLimitChange = (limit: number) => {
        fetchAvailablePlayers(1, limit, searchQuery)
    }

    const handleSearchChange = (value: string) => {
        setSearchQuery(value)
        
        // Clear existing timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current)
        }

        // Set new timeout for debounced search
        searchTimeoutRef.current = setTimeout(() => {
            fetchAvailablePlayers(1, pagination.per_page, value)
        }, 500)
    }

    const handleTeamPlayersPageChange = (page: number) => {
        fetchTeamDetails(page, teamPlayersPagination.per_page)
    }

    const handleTeamPlayersLimitChange = (limit: number) => {
        fetchTeamDetails(1, limit)
    }

    useEffect(() => {
        fetchTeamDetails()
        fetchDropdownData()
    }, [team_id])

    useEffect(() => {
        if (isOffcanvasOpen) {
            fetchAvailablePlayers(1, 20, "")
        } else {
            setAvailablePlayers([])
            setSearchQuery("")
            setSelectedPlayers([])
            setPagination({
                current_page: 1,
                per_page: 20,
                total: 0,
                total_pages: 1,
                has_next_page: false,
                has_prev_page: false,
                next_page: null,
                prev_page: null
            })
            
            // Clear search timeout when closing offcanvas
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current)
            }
        }
    }, [isOffcanvasOpen, fetchAvailablePlayers])

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current)
            }
        }
    }, [])

    if (loading) {
        return (
            <div className="w-full min-h-screen bg-white p-4 md:p-6">
                <div className="flex justify-center items-center h-96">
                    <Loader />
                </div>
            </div>
        )
    }

    const team = teamData?.team
    const owner = teamData?.owner
    const captain = teamPlayers.find((player) => player.is_captain)
    const wicketKeepers = teamPlayers.filter((player) => player.is_wicket_keeper) || []

    const SelectedPlayersForm = () => (
        <div className="space-y-2">
            {selectedPlayers.map((player) => (
                <div key={player.id} className="p-3 border rounded-lg bg-white">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                                <span className="text-primary-600 font-medium text-xs">
                                    {player.player_name?.charAt(0) || player.details?.first_name?.charAt(0) || "P"}
                                </span>
                            </div>
                            <div className="text-sm font-medium text-gray-900">
                                {player.player_name || `${player.details?.first_name} ${player.details?.last_name}`}
                            </div>
                        </div>
                        <button
                            onClick={() => handleRemoveSelectedPlayer(player.id)}
                            className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors"
                        >
                            <FiTrash2 className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Jersey #</label>
                            <input
                                type="number"
                                value={player.jersey_number || ""}
                                onChange={(e) => updateSelectedPlayer(player.id, "jersey_number", e.target.value ? parseInt(e.target.value) : null)}
                                className="w-full p-1.5 border rounded text-xs"
                                placeholder="Optional"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Role</label>
                            <select
                                value={player.player_role || player.role_id}
                                onChange={(e) => updateSelectedPlayer(player.id, "player_role", parseInt(e.target.value))}
                                className="w-full p-1.5 border rounded text-xs"
                            >
                                {playerRoles.map(role => (
                                    <option key={role.id} value={role.id}>{role.name}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Batting</label>
                            <select
                                value={player.batting_style || ""}
                                onChange={(e) => updateSelectedPlayer(player.id, "batting_style", e.target.value)}
                                className="w-full p-1.5 border rounded text-xs"
                            >
                                {battingStyles.map(style => (
                                    <option key={style.id} value={style.id}>{style.name}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Bowling</label>
                            <select
                                value={player.bowling_style || ""}
                                onChange={(e) => updateSelectedPlayer(player.id, "bowling_style", e.target.value)}
                                className="w-full p-1.5 border rounded text-xs"
                            >
                                {bowlingStyles.map(style => (
                                    <option key={style.id} value={style.id}>{style.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="flex items-center gap-3 mt-2">
                        <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                            <input
                                type="checkbox"
                                checked={player.is_captain || false}
                                onChange={(e) => updateSelectedPlayer(player.id, "is_captain", e.target.checked)}
                                className="rounded"
                            />
                            Captain
                        </label>
                        <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                            <input
                                type="checkbox"
                                checked={player.is_wicket_keeper || false}
                                onChange={(e) => updateSelectedPlayer(player.id, "is_wicket_keeper", e.target.checked)}
                                className="rounded"
                            />
                            Wicket Keeper
                        </label>
                    </div>
                </div>
            ))}
        </div>
    )

    return (
        <div className="w-full min-h-screen bg-white p-4 md:p-6">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                    <Link
                        to="/teams"
                        className="flex items-center gap-2 px-3 py-2 bg-white border rounded-lg hover:bg-gray-50"
                    >
                        <FiArrowLeft className="w-4 h-4" />
                        Back
                    </Link>
                    <h2 className="text-xl font-bold text-primary-950 flex items-center">
                        <span className="bg-primary-700 w-1 h-6 mr-3 rounded"></span>
                        Team Details
                    </h2>
                </div>
                <Breadcrumb />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-4">
                <div className="lg:col-span-1">
                    <div className="bg-white rounded-lg shadow border p-6">
                        <div className="text-center mb-6">
                            {team?.logo_url ? (
                                <img
                                    src={team.logo_url}
                                    alt={team?.name || "Team"}
                                    className="w-24 h-24 rounded-full mx-auto mb-4 object-cover border-4 border-primary-100"
                                />
                            ) : (
                                <div className="w-24 h-24 rounded-full mx-auto mb-4 bg-primary-100 flex items-center justify-center border-4 border-primary-200">
                                    <FiShield className="w-12 h-12 text-primary-600" />
                                </div>
                            )}
                            <h3 className="text-xl font-bold text-gray-900 mb-2">{team?.name || "Unknown Team"}</h3>
                            <span
                                className={`px-3 py-1 rounded-full text-sm font-medium ${team?.is_active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}
                            >
                                {team?.is_active ? "Active" : "Inactive"}
                            </span>
                        </div>

                        <div className="space-y-4">
                            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                <FiMapPin className="w-5 h-5 text-gray-600" />
                                <div>
                                    <div className="text-sm text-gray-600">Location</div>
                                    <div className="font-medium">{team?.location || "Not specified"}</div>
                                </div>
                            </div>

                            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                <FiUsers className="w-5 h-5 text-gray-600" />
                                <div>
                                    <div className="text-sm text-gray-600">Total Players</div>
                                    <div className="font-medium">{teamPlayersPagination.total || 0}</div>
                                </div>
                            </div>

                            {captain && (
                                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                    <FiStar className="w-5 h-5 text-yellow-600" />
                                    <div>
                                        <div className="text-sm text-gray-600">Captain</div>
                                        <div className="font-medium">{captain.player_name}</div>
                                        <div className="text-sm text-gray-500">
                                            {captain.jersey_number ? `Jersey #${captain.jersey_number}` : 'No jersey number'}
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                <FiCalendar className="w-5 h-5 text-gray-600" />
                                <div>
                                    <div className="text-sm text-gray-600">Created At</div>
                                    <div className="font-medium">{formatDate(team?.created_at || "")}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {owner && (
                        <div className="bg-white rounded-lg shadow border p-6 mt-6">
                            <h4 className="text-lg font-semibold mb-4 text-gray-900">Owner Information</h4>
                            <div className="space-y-3">
                                <div>
                                    <div className="text-sm text-gray-600">Name</div>
                                    <div className="font-medium">
                                        {owner.details?.first_name} {owner.details?.last_name}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-sm text-gray-600">Phone</div>
                                    <div className="font-medium">{owner.phone_number}</div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="lg:col-span-2">
                    <div className="bg-white rounded-lg shadow border">
                        <div className="p-6 border-b">
                            <div className="flex items-center justify-between">
                                <h4 className="text-lg font-semibold text-gray-900">Players ({teamPlayersPagination.total || 0})</h4>
                                <button
                                    onClick={() => setIsOffcanvasOpen(true)}
                                    className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                                >
                                    <FiPlus className="w-4 h-4" />
                                    Add Players
                                </button>
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            {teamPlayers && teamPlayers.length > 0 ? (
                                <>
                                    <table className="w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="p-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Player</th>
                                                <th className="p-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Jersey #</th>
                                                <th className="p-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                                                <th className="p-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                                <th className="p-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {teamPlayers.map((player) => (
                                                <tr key={player.id} className="hover:bg-gray-50">
                                                    <td className="p-4 whitespace-nowrap">
                                                        <div className="flex items-center">
                                                            <div className="flex-shrink-0 h-10 w-10">
                                                                <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                                                                    <span className="text-primary-600 font-medium text-sm">
                                                                        {player.player_name?.charAt(0) || "?"}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            <div className="ml-4">
                                                                <div className="text-sm font-medium text-gray-900">
                                                                    {player.player_name || "Unknown Player"}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="p-4 whitespace-nowrap">
                                                        <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-sm font-medium">
                                                            {player.jersey_number ? `#${player.jersey_number}` : "-"}
                                                        </span>
                                                    </td>
                                                    <td className="p-4 whitespace-nowrap">
                                                        <span className="text-sm text-gray-900">{getPlayerRole(player.player_role || 0)}</span>
                                                    </td>
                                                    <td className="p-4 whitespace-nowrap">
                                                        <div className="flex gap-2">
                                                            {player.is_captain && (
                                                                <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                                                                    <FiStar className="w-3 h-3" />
                                                                    Captain
                                                                </span>
                                                            )}
                                                            {player.is_wicket_keeper && (
                                                                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                                                                    <FiShield className="w-3 h-3" />
                                                                    WK
                                                                </span>
                                                            )}
                                                            {!player.is_captain && !player.is_wicket_keeper && (
                                                                <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-xs font-medium">
                                                                    Player
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="p-4 whitespace-nowrap">
                                                        <div className="flex items-center gap-2">
                                                            <button
                                                                onClick={() => handleEditPlayer(player)}
                                                                className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                                                                title="Edit Player"
                                                            >
                                                                <FiEdit className="w-4 h-4" />
                                                            </button>
                                                            <button
                                                                onClick={() => removePlayerFromTeam(player.id)}
                                                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                                title="Remove Player"
                                                            >
                                                                <FiTrash2 className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </>
                            ) : (
                                <div className="text-center py-12">
                                    <FiUsers className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                    <div className="text-gray-500">No players found</div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {teamPlayers?.length > 0 && <Pagination
                pagination={teamPlayersPagination}
                onPageChange={handleTeamPlayersPageChange}
                onLimitChange={handleTeamPlayersLimitChange}
            />}

            {isEditModalOpen && editingPlayer && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between p-6 border-b bg-gray-50">
                            <h3 className="text-lg font-semibold text-gray-900">Edit Player - {editingPlayer.player_name}</h3>
                            <button onClick={() => setIsEditModalOpen(false)} className="p-2 hover:bg-gray-200 rounded-lg transition-colors">
                                <FiX className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
                            <div className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Jersey Number</label>
                                        <input
                                            type="number"
                                            min="1"
                                            max="99"
                                            value={editingPlayer.jersey_number || ""}
                                            onChange={(e) => updateEditingPlayer("jersey_number", e.target.value ? parseInt(e.target.value) : null)}
                                            className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                                            placeholder="Optional (1-99)"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Player Role</label>
                                        <select
                                            value={editingPlayer.player_role || ""}
                                            onChange={(e) => updateEditingPlayer("player_role", parseInt(e.target.value))}
                                            className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                                        >
                                            {playerRoles.map(role => (
                                                <option key={role.id} value={role.id}>{role.name}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Batting Style</label>
                                        <select
                                            value={editingPlayer.batting_style || ""}
                                            onChange={(e) => updateEditingPlayer("batting_style", parseInt(e.target.value))}
                                            className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                                        >
                                            {battingStyles.map(style => (
                                                <option key={style.id} value={style.id}>{style.name}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Bowling Style</label>
                                        <select
                                            value={editingPlayer.bowling_style || ""}
                                            onChange={(e) => updateEditingPlayer("bowling_style", parseInt(e.target.value))}
                                            className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                                        >
                                            {bowlingStyles.map(style => (
                                                <option key={style.id} value={style.id}>{style.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="flex items-center gap-6 pt-4 border-t">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={editingPlayer.is_captain || false}
                                            onChange={(e) => updateEditingPlayer("is_captain", e.target.checked)}
                                            className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">Captain</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={editingPlayer.is_wicket_keeper || false}
                                            onChange={(e) => updateEditingPlayer("is_wicket_keeper", e.target.checked)}
                                            className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">Wicket Keeper</span>
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
                            <button
                                onClick={() => setIsEditModalOpen(false)}
                                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={savePlayerChanges}
                                disabled={isUpdatingPlayer}
                                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                <FiSave className="w-4 h-4" />
                                {isUpdatingPlayer ? "Saving..." : "Save Changes"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isOffcanvasOpen && (
                <>
                    <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setIsOffcanvasOpen(false)}></div>

                    <div className="fixed right-0 top-0 h-full w-full md:w-[90%] lg:w-[75%] bg-white shadow-xl z-50 transform transition-transform duration-300 overflow-hidden">
                        <div className="flex flex-col h-full">
                            <div className="flex items-center justify-between p-6 border-b bg-gray-50">
                                <h3 className="text-lg font-semibold text-gray-900">Manage Players</h3>
                                <button onClick={() => setIsOffcanvasOpen(false)} className="p-2 hover:bg-gray-200 rounded-lg transition-colors">
                                    <FiX className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="flex-1 overflow-hidden flex flex-col">
                                {selectedPlayers.length > 0 && (
                                    <div className="border-b bg-gray-50 p-4">
                                        <div className="flex justify-between items-center">
                                            <h4 className="text-md font-medium text-gray-900">Selected Players ({selectedPlayers.length})</h4>
                                            <div className="flex items-center gap-3">
                                                <button
                                                    onClick={() => setIsModalOpen(true)}
                                                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                                                >
                                                    <FiEye className="w-4 h-4" />
                                                    View Selected Players
                                                </button>

                                                <button
                                                    onClick={addPlayersToTeam}
                                                    disabled={isAddingPlayers || selectedPlayers.length === 0}
                                                    className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                                >
                                                    {isAddingPlayers ? "Adding..." : `Add (${selectedPlayers.length}) Players`}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="flex-1 overflow-hidden flex flex-col p-6">
                                    <div className="mb-4">
                                        <h4 className="text-md font-medium text-gray-900 mb-3">Available Players</h4>
                                        <div className="relative">
                                            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                            <input
                                                type="text"
                                                placeholder="Search players by name or phone..."
                                                value={searchQuery}
                                                onChange={(e) => handleSearchChange(e.target.value)}
                                                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                                            />
                                        </div>
                                    </div>

                                    <div className="flex-1 overflow-y-auto mb-4">
                                        {isLoadingPlayers ? (
                                            <div className="flex justify-center items-center py-12">
                                                <Loader />
                                            </div>
                                        ) : availablePlayers.length > 0 ? (
                                            <div className="space-y-2">
                                                {availablePlayers.map((player) => (
                                                    <div
                                                        key={player.id}
                                                        className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                                                                <span className="text-primary-600 font-medium text-sm">
                                                                    {player.details?.first_name?.charAt(0) || "P"}
                                                                </span>
                                                            </div>
                                                            <div>
                                                                <div className="text-sm font-medium text-gray-900">
                                                                    {player.details?.first_name} {player.details?.last_name}
                                                                </div>
                                                                <div className="text-xs text-gray-500">
                                                                    {player.role_name} • {player.phone_number}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <button
                                                            onClick={() => handleAddSelectedPlayer(player)}
                                                            disabled={player.is_in_team || selectedPlayers.some((p) => p.id === player.id)}
                                                            className="flex items-center gap-1 px-3 py-1.5 bg-primary-600 text-white text-xs rounded hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                                                        >
                                                            <FiPlus className="w-3 h-3" />
                                                            {player.is_in_team ? "In Team" : selectedPlayers.some((p) => p.id === player.id) ? "Selected" : "Select"}
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="text-center py-12">
                                                <FiUsers className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                                <div className="text-gray-500">
                                                    {searchQuery ? "No players found matching your search" : "No players found"}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {!isLoadingPlayers && availablePlayers.length > 0 && (
                                        <Pagination
                                            pagination={pagination}
                                            onPageChange={handlePageChange}
                                            onLimitChange={handleLimitChange}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {isModalOpen && (
                <>
                    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setIsModalOpen(false)}>
                        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-between p-6 border-b bg-gray-50">
                                <h3 className="text-lg font-semibold text-gray-900">Selected Players ({selectedPlayers.length})</h3>
                                <button onClick={() => setIsModalOpen(false)} className="p-2 hover:bg-gray-200 rounded-lg transition-colors">
                                    <FiX className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
                                {selectedPlayers.length > 0 ? (
                                    <SelectedPlayersForm />
                                ) : (
                                    <div className="text-center py-12">
                                        <FiUsers className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                        <div className="text-gray-500">No players selected</div>
                                    </div>
                                )}
                            </div>

                            <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
                                <button
                                    onClick={() => setIsModalOpen(false)}
                                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                                >
                                    Close
                                </button>
                                <button
                                    onClick={addPlayersToTeam}
                                    disabled={isAddingPlayers || selectedPlayers.length === 0}
                                    className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    {isAddingPlayers ? "Adding..." : `Add All Players (${selectedPlayers.length})`}
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}

            <ConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={cancelDeletePlayer}
                onConfirm={confirmDeletePlayer}
                title="Remove Player"
                description="Are you sure you want to remove this player from the team? This action cannot be undone."
                loading={isDeletingPlayer}
            />
        </div>
    )
}

export default TeamView